import { useEffect, useState } from "react";
import { Checkbox, RadioButton } from "@progress/kendo-react-inputs";
import { Circle } from "phosphor-react-sc";
import { NavigationArrow, Square, Triangle, MapPin } from "phosphor-react-sc";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { MapPanelTabContent } from "./MapPanelTabContent";
import Switch from "../../switch";
import Spacer from "../../Spacer";
import { isArrayEmpty, isBoolean, isObjectNull } from "../../../helpers/ObjectHelpers";
import { getElementById } from "../../../helpers/ElementHelpers";
import { IconColors } from "../../../../services/SystemNames";
import { IMapParamsService } from "../services/MapParamsService";
import { sleep } from "../../../helpers/DataHelpers";
import { getBooleanSearchParam } from "../../../helpers/UrlHelpers";
import { useSearchParams } from "react-router";
import {
    useSetShowLocations,
    useShowLocations,
    useSetActivateSeamap,
    useSelectShipPosition,
    useShowLegend,
    useSetShowLegend,
    useSetShowLocationType,
    useShowLocationType,
    useMapId,
    useShowLocationQuayManual,
    useSetShowLocationQuayManual,
    useHasQuayManualAccess,
} from "../hooks/useMapStore";

interface MapPanelSettingsProps {
    pilotage?: any,
    pecExam?: any,
    showAis?: boolean,
    panelTabContentHeight: number,
    paramsService: IMapParamsService,
}

export const MapPanelSettings = (
    {
        pilotage = null,
        showAis = false,
        pecExam,
        panelTabContentHeight,
        paramsService,
    }: MapPanelSettingsProps) => {

    const [selectedTab, setSelectedTab] = useState(0);

    const handleSelectTab = (e:any) => {
        setSelectedTab(e.selected);
    };
    
    return (
        <div className={`google-map-settings google-map-panel`}>
            <div className="google-map-settings-content">
                <TabStrip selected={selectedTab} onSelect={handleSelectTab}>
                    <TabStripTab title="Grunnlag">
                        <MapPanelTabContent panelTabContentHeight={panelTabContentHeight}>
                            <MapSettingsView
                                paramsService={paramsService}
                                pecExam={pecExam}
                                pilotage={pilotage}
                            />
                        </MapPanelTabContent>
                    </TabStripTab>
                    <TabStripTab title="Informasjon">
                        <MapPanelTabContent panelTabContentHeight={panelTabContentHeight}>
                            <MapSettingsInfo
                                pilotage={pilotage}
                                showAis={showAis}/>
                            </MapPanelTabContent>
                    </TabStripTab>
                </TabStrip>
            </div>
        </div>
    )
}

interface MapSettingsViewProps {
    paramsService: IMapParamsService,
    shipPosition?: any,
    pecExam?: any,
    pilotage?: any,
}

const MapSettingsView = ({
    paramsService,
    pecExam,
    pilotage
}: MapSettingsViewProps) => {

    const mapId = useMapId();
    const setShowLocations = useSetShowLocations();
    const showLocations = useShowLocations();
    const setActivateSeamap = useSetActivateSeamap();
    const stateShowLegend = useShowLegend();
    const stateSetShowLegend = useSetShowLegend();
    const stateSelectShipPosition = useSelectShipPosition();
    const setShowLocationType = useSetShowLocationType();
    const showLocationType = useShowLocationType();
    const showLocationQuayManual = useShowLocationQuayManual();
    const setShowLocationQuayManual = useSetShowLocationQuayManual();
    const { hasQuayManualAccess } = useHasQuayManualAccess();
    
    const [searchParams, _] = useSearchParams();

    const [isMapActive, setIsMapActive] = useState(true);
    const [isMapTerrainSelected, setIsMapTerrainSelected] = useState(false);
    const [isSatelliteActive, setIsSatelliteActive] = useState(false);
    const [isSatelliteLabelsSelected, setIsSatelliteLabelsSelected] = useState(true);
    const [isSeamapActive, setIsSeamapActive] = useState(false);

    const [showLegend, setShowLegend] = useState(getBooleanSearchParam(searchParams, "sleg"));
    const [shipPosition, setShipPosition] = useState<any>(null);

    useEffect(() => {
        setShowLegend(stateShowLegend);
    }, [stateShowLegend]);

    useEffect(() => {
        setShipPosition(stateSelectShipPosition);
    }, [stateSelectShipPosition]);
    
    useEffect(() => {
        const initMap = async () => {
            const val = paramsService.showMap;
            if (!isBoolean(val)) return;
            if (!val!) return;

            await waitForRadioElement("Kart");

            onMapChange();
        }
        const initMapTerrain = async () => {
            const val = paramsService.showMapTerrain;
            if (!isBoolean(val)) return;
            if (!val!) return;
            await waitForCheckboxElement(0);
            onChange(0, setIsMapTerrainSelected, val!);
        }
        const initSatellite = async () => {
            const val = paramsService.showSatellite;
            if (!isBoolean(val)) return;
            if (!val!) return;

            await waitForRadioElement("Satellitt");

            onSatelliteChange(); 
        }
        const initSatelliteLabels = async () => {
            const val = paramsService.showSatelliteLabels;
            if (!isBoolean(val)) return;
            if (val!) return;

            await waitForCheckboxElement(1);
            onChange(1, setIsSatelliteLabelsSelected, val!);
        }
        const initSeamap = () => {
            const val = paramsService.showSeamap;
            if (!isBoolean(val)) return;
            handleOnSeaMapChange(val!);
        }
        const initLocations = () => {
            const val = paramsService.showLocations;
            if (!isBoolean(val)) return;
            setIsLocationsActive(val!);
        }
        
        initMap();
        initMapTerrain();
        initSatellite();
        initSatelliteLabels();
        initSeamap();
        initLocations();
    }, []);

    return (
        <div>
            <Spacer/>
            <div className="google-map-settings-alternative">
                <RadioButton 
                    size="large"
                    checked={isMapActive}
                    onChange={onMapChange}
                    label="Kart"
                />
            </div>
            <Spacer height={5} />
            <div className="google-map-settings-switch-alternative">
                <Checkbox
                    size="large"
                    label="Terreng"
                    disabled={!isMapActive}
                    onChange={onMapTerrainChange}
                    checked={isMapTerrainSelected} />
            </div>
            <Spacer height={10} />
            <div className="google-map-settings-alternative">
                <RadioButton
                    size="large"
                    checked={isSatelliteActive}
                    onChange={onSatelliteChange}
                    label="Satellitt"
                />
            </div>
            <Spacer height={5} />
            <div className="google-map-settings-switch-alternative">
                <Checkbox
                    size="large"
                    label="Etiketter"
                    disabled={!isSatelliteActive}
                    onChange={onSatelliteLabelsChange}
                    checked={isSatelliteLabelsSelected} />
            </div>
            <Spacer />
            <div className="google-map-settings-header">Grunnlag</div>
            <div className="google-map-settings-alternative">
                <Switch
                    onLabel="Sjøkart"
                    size="small"
                    fontSize={16}
                    autoFontSize={false}
                    bold={false}
                    checked={isSeamapActive}
                    onChange={() => {
                        handleOnSeaMapChange(!isSeamapActive);
                    }}
                />
            </div>
            <Spacer />
            <div className="google-map-settings-header">Vis</div>
            {
                (shipPosition || pecExam || pilotage) &&
                <>
                    <div className="google-map-settings-alternative">
                        <Switch
                            onLabel="Legend"
                            size="small"
                            fontSize={16}
                            autoFontSize={false}
                            bold={false}
                            checked={showLegend}
                            onChange={onLegendChange}
                        />
                    </div>
                    <Spacer height={5} />
                </>
            }
            <div className="google-map-settings-alternative">
                <Switch
                    onLabel="Lokasjoner"
                    size="small"
                    fontSize={16}
                    autoFontSize={false}
                    bold={false}
                    checked={showLocations}
                    onChange={() => {
                        const active = !showLocations;
                        setShowLocations(active);
                        paramsService.onShowLocationsChange(!active);
                    }}
                />
                <Spacer height={10} />
            </div>
             <div className="google-map-settings-switch-alternative">
                <Checkbox
                    size="large"
                    label="Typefarge"
                    disabled={!showLocations}
                    onChange={() => setShowLocationType(!showLocationType) }
                    checked={showLocationType}
                />
                <Spacer height={10} />
                {
                    hasQuayManualAccess &&
                    <Checkbox
                        size="large"
                        label="Kaibok"
                        disabled={!showLocations}
                        onChange={() => setShowLocationQuayManual(!showLocationQuayManual)}
                        checked={showLocationQuayManual}
                    />
                }
            </div>
        </div>
    );

    function handleOnSeaMapChange(e: boolean) {
        setIsSeamapActive(e);
        setActivateSeamap(e);
        paramsService.onShowSeamapChange(e);
    }

    function onMapChange() {
        if (isMapActive) return;

        paramsService.onShowMapChange(true);

        setIsMapActive(true);
        setIsSatelliteActive(false);

        onClick("Kart")
    }

    function onMapTerrainChange() {
        onChange(0, setIsMapTerrainSelected, !isMapTerrainSelected);
        paramsService.onShowMapTerrainChange(!isMapTerrainSelected);
    }

    function onSatelliteChange() {
        if (isSatelliteActive) return;
        
        paramsService.onShowSatelliteChange(true);

        setIsSatelliteActive(true);
        setIsMapActive(false);
        
        onClick("Satellitt")
    }

    function onSatelliteLabelsChange() {
        onChange(1, setIsSatelliteLabelsSelected, !isSatelliteLabelsSelected);
        paramsService.onShowSatelliteLabelsChange(!isSatelliteLabelsSelected);
    }

    function onClick(text: string) {
        const elem = findRadioElementByText(text);
        if (!elem) return;
        elem.click();
    }

    function onChange(menuIndex: number, set: (e: any) => void, val: any) {
        const mapMenuItem = findCheckboxElementByIndex(menuIndex);
        
        if (!mapMenuItem) return;
        mapMenuItem.click();

        set(val);
    }

    async function waitForRadioElement(text: string) {
        await waitForElement(() => findRadioElementByText(text));
    }

    async function waitForCheckboxElement(menuIndex: number) {
        await waitForElement(() => findCheckboxElementByIndex(menuIndex));
    }

    async function waitForElement(find: () => void) {
        let elem = null;
        let tries = 0;
        while (elem === null) {
            elem = find();
            await sleep(125);
            tries++;
            if (tries > 10) break;
        }
    }

    function findRadioElementByText(text: string) {

        const mapElem = getElementById(mapId);
        if (!mapElem) return;

        let button = null;
        const elems: NodeListOf<HTMLElement> = mapElem.querySelectorAll('[role="menuitemradio"]');

        for (const elem of elems) {
            if (elem.innerText.toUpperCase() === text.toUpperCase()) {
                button = elem;
            }
        }

        return button;
    }

    function findCheckboxElementByIndex(menuIndex: number) {
        const mapElem = getElementById(mapId);
        if (!mapElem) return null;

        const elem = mapElem.querySelector('[role="menubar"]');
        if (!elem) return null;

        const menus = elem.querySelectorAll('[role="menu"]');
        if (isArrayEmpty(menus)) return null;

        const mapMenu = menus[menuIndex];
        const mapMenuItem: HTMLElement | null = mapMenu.querySelector('[role="menuitemcheckbox"]')

        return mapMenuItem;
    }

    function onLegendChange() {
        stateSetShowLegend(!showLegend);
        paramsService.onShowLegendChange(!showLegend);
        setShowLegend(!showLegend);
    }
}

interface MapSettingsInfoProps {
    pilotage: any,
    showAis?: boolean | null
}

const MapSettingsInfo = ({ pilotage, showAis = false }: MapSettingsInfoProps) => {
    return (
        <div>
            {
                (!isObjectNull(pilotage)) &&
                <>
                    <div className="google-map-settings-header">Oppdrag</div>
                    <div className="google-map-settings-legend">
                        <MapPanelSettingsInfoLocation color={IconColors.PinStart} text={`Fra: ${pilotage.fromLocation.name}`} />
                        <MapPanelSettingsInfoLocation color={IconColors.PinStop} text={`Til: ${pilotage.toLocation.name}`} />
                    </div>
                </>
            }
            <div className="google-map-settings-header">Lokasjoner</div>
            <div className="google-map-settings-legend">
                <MapPanelSettingsInfoLocationType color="var(--map-pin-location-harbour-bg)" text="Havn" />
                <MapPanelSettingsInfoLocationType color="var(--map-pin-location-pilot-boarding-bg)" text="Losbordingspunkt" />
                <MapPanelSettingsInfoLocationType color="var(--map-pin-location-location-at-sea-bg)" text="Sted i sjøen" />
                <MapPanelSettingsInfoLocationType color="var(--map-pin-location-anchorage-bg)" text="Ankerplass" />
                <Spacer />
                <div>
                    <div>
                        <Circle
                            size={16}
                            weight="fill"
                            className="svg-icon"
                            color={IconColors.ShipDefault}
                        />
                    </div>
                    <div>Standard låsingsfrist</div>
                </div>
                <div>
                    <div>
                        <Triangle
                            size={16}
                            weight="fill"
                            className="svg-icon"
                            color={IconColors.ShipDefault}
                            style={{
                                transform: `rotate(180deg)`
                            }}
                        />
                    </div>
                    <div>Avvikende låsingsfrist</div>
                </div>
                <Spacer />
                <MapPanelSettingsInfoShipSelectedType color="var(--map-ship-selected-bg)" text="Valgt lokasjon" />
            </div>
            {
                (!isObjectNull(pilotage) || showAis) &&
                <>
                    <div className="google-map-settings-header">Fartøy</div>
                    <div className="google-map-settings-legend">
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipPilotVessel} text="Tilbringer" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipCargo} text="Fraktbåt" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipTanker} text="Tankbåt" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipPassenger} text="Passasjerbåt" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipFish} text="Fiskebåt" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipTug} text="Taubåt" />
                        <MapPanelSettingsInfoVesselType color={IconColors.ShipDefault} text="Annet/Ukjent" />
                    </div>
                    <Spacer />
                    <div className="google-map-settings-legend">
                        <div>
                            <div>
                                <Square
                                    size={16}
                                    weight="fill"
                                    className="svg-icon"
                                    color={IconColors.ShipDefault}
                                    style={{
                                        transform: `rotate(45deg)`
                                    }}
                                />
                            </div>
                            <div>&nbsp;Fortøyd/forankret</div>
                        </div>
                        <MapPanelSettingsInfoVessel color={IconColors.ShipDefault} text="Underveis" />
                        <Spacer />
                        <MapPanelSettingsInfoVessel color={IconColors.ShipDefault} text="&nbsp;&nbsp;&nbsp;&nbsp;Mindre enn 75m" size={16} />
                        <MapPanelSettingsInfoVessel color={IconColors.ShipDefault} text="&nbsp;&nbsp;Mellom 75m og 150m" size={24} />
                        <MapPanelSettingsInfoVessel color={IconColors.ShipDefault} text="Større enn 150m" size={32} />
                        <Spacer />
                        <MapPanelSettingsInfoShipSelectedType color="var(--map-ship-selected-bg)" text="Valgt fartøy" />
                        <MapPanelSettingsInfoShipSelectedType color="var(--map-ship-highlight-bg)" text="Losoppdrag fartøy" />
                    </div>
                </>
            }
            <Spacer/>
        </div>
    );
}

interface MapPanelSettingsInfoLocationTypeProps {
    color: string,
    text: string
}

const MapPanelSettingsInfoLocationType = ({ color, text }: MapPanelSettingsInfoLocationTypeProps) => {
    return (
        <div>
            <div>
                <Circle weight="fill" color={color} className="svg-icon" />
                &nbsp;&nbsp;
                <Triangle weight="fill" color={color} className="svg-icon" style={{
                    transform: 'rotate(180deg)'
                }} />
            </div>
            <div>{text}</div>
        </div>
    );
}

interface MapPanelSettingsInfoVesselTypeProps {
    color: string,
    text: string
}

const MapPanelSettingsInfoVesselType = ({ color, text }: MapPanelSettingsInfoVesselTypeProps) => {
    return (
        <div>
            <div>
                <Square
                    weight="fill"
                    color={color}
                    className="svg-icon"
                    size={16}
                    style={{
                        transform: `rotate(45deg)`
                    }}
                />&nbsp;&nbsp;
                <NavigationArrow
                    size={20}
                    weight="fill"
                    className="svg-icon"
                    color={color}
                    style={{
                        transform: `rotate(135deg)`
                    }}
                />
            </div>
            <div>{text}</div>
        </div>
    );
}

interface MapPanelSettingsInfoVesselProps {
    color: string,
    text: string,
    size?: number
}

const MapPanelSettingsInfoVessel = ({ color, text, size = 20 }: MapPanelSettingsInfoVesselProps) => {
    return (
        <div>
            <div>
                <NavigationArrow
                    size={size}
                    weight="fill"
                    className="svg-icon"
                    color={color}
                    style={{
                        transform: `rotate(135deg)`
                    }}
                />
            </div>
            <div>{text}</div>
        </div>
    );
}

interface MapPanelSettingsInfoLocationProps {
    color: string,
    text: string
}

const MapPanelSettingsInfoLocation = ({ color, text }: MapPanelSettingsInfoLocationProps) => {
    return (
        <div>
            <div><MapPin weight="fill" color={color} size={30} className="svg-icon" /></div>
            <div>{text}</div>
        </div>
    );
}

interface MapPanelSettingsInfoShipSelectedTypeProps {
    color: string,
    text: string
}

const MapPanelSettingsInfoShipSelectedType = ({ color, text }: MapPanelSettingsInfoShipSelectedTypeProps) => {
    return (
        <div>
            <div>
                <div style={{
                    width: 24,
                    height: 24,
                    border: "dashed 2px var(--map-ship-border)",
                    background: color,
                    opacity: 0.5,
                    borderRadius: "50%",
                }}>
                </div>
            </div>
            <div>{text}</div>
        </div>
    );
}
